import { createFileRoute } from "@tanstack/react-router";
import { requireAdmin } from "@/lib/admin-guard";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  Mail,
  CalendarPlus,
  Eye,
  Loader2,
  Search,
  CalendarDays,
  Table as TableIcon,
} from "lucide-react";
import { toast } from "sonner";
import { THERAPISTS, type Therapist } from "@/lib/therapists-data";
import {
  listTherapistEventProfiles,
  requestTherapistAvailability,
  scheduleTherapistEvent,
  listTherapistResponses,
} from "@/lib/therapist-events.functions";

export const Route = createFileRoute("/admin_/therapist-events")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Therapist Events — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdmin,
  component: TherapistEventsAdmin,
});

type Row = {
  therapist: Therapist;
  profile?: {
    therapist_id: string;
    status: string;
    availability_status: string;
    last_event_at: string | null;
  };
  latestRequest?: {
    status: string;
    sent_at: string;
    response_token: string;
  };
};

function TherapistEventsAdmin() {
  const [tab, setTab] = useState<"table" | "calendar">("table");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selected, setSelected] = useState<Therapist | null>(null);
  const [dialog, setDialog] = useState<"request" | "schedule" | "responses" | null>(null);

  const listFn = useServerFn(listTherapistEventProfiles);
  const { data, isLoading } = useQuery({
    queryKey: ["therapist-event-profiles"],
    queryFn: () => listFn(),
  });

  const rows: Row[] = useMemo(() => {
    const profileMap = new Map((data?.profiles ?? []).map((p) => [p.therapist_id, p]));
    const latestReqMap = new Map<string, Row["latestRequest"]>();
    (data?.requests ?? []).forEach((r) => {
      if (!latestReqMap.has(r.therapist_id)) {
        latestReqMap.set(r.therapist_id, {
          status: r.status,
          sent_at: r.sent_at,
          response_token: r.response_token,
        });
      }
    });
    return THERAPISTS.map((t) => ({
      therapist: t,
      profile: profileMap.get(t.id) as Row["profile"],
      latestRequest: latestReqMap.get(t.id),
    }));
  }, [data]);

  const filtered = rows.filter((r) => {
    const q = search.toLowerCase();
    const matches =
      !q ||
      r.therapist.name.toLowerCase().includes(q) ||
      r.therapist.contactEmail.toLowerCase().includes(q) ||
      r.therapist.country.toLowerCase().includes(q) ||
      r.therapist.specialties.join(" ").toLowerCase().includes(q);
    const status = r.latestRequest?.status ?? "none";
    const matchStatus = statusFilter === "all" || status === statusFilter;
    return matches && matchStatus;
  });

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl text-foreground">Therapist Events</h1>
          <p className="text-sm text-muted-foreground">
            Request availability, schedule live sessions, and review responses.
          </p>
        </div>
        <div className="inline-flex rounded-full border border-border bg-card p-1 text-sm">
          <button
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 ${
              tab === "table" ? "bg-foreground text-background" : "text-muted-foreground"
            }`}
            onClick={() => setTab("table")}
          >
            <TableIcon className="h-3.5 w-3.5" /> Roster
          </button>
          <button
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 ${
              tab === "calendar" ? "bg-foreground text-background" : "text-muted-foreground"
            }`}
            onClick={() => setTab("calendar")}
          >
            <CalendarDays className="h-3.5 w-3.5" /> Calendar
          </button>
        </div>
      </header>

      {tab === "table" && (
        <>
          <div className="mb-4 flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                className="w-full rounded-lg border border-border bg-card py-2 ps-9 pe-3 text-sm"
                placeholder="Search by name, country, specialism…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select
              className="rounded-lg border border-border bg-card px-3 py-2 text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All statuses</option>
              <option value="none">Not contacted</option>
              <option value="pending">Pending</option>
              <option value="responded">Responded</option>
              <option value="declined">Declined</option>
            </select>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-20 text-muted-foreground">
              <Loader2 className="me-2 h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-border bg-card">
              <table className="w-full text-sm">
                <thead className="bg-muted/30 text-start text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Country</th>
                    <th className="px-4 py-3">Specialism</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Last Event</th>
                    <th className="px-4 py-3">Availability</th>
                    <th className="px-4 py-3 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const status = r.latestRequest?.status ?? "none";
                    return (
                      <tr key={r.therapist.id} className="border-t border-border">
                        <td className="px-4 py-3 font-medium text-foreground">{r.therapist.name}</td>
                        <td className="px-4 py-3 text-muted-foreground">{r.therapist.contactEmail}</td>
                        <td className="px-4 py-3 text-muted-foreground">{r.therapist.country}</td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {r.therapist.specialties.slice(0, 2).join(", ")}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={status} />
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">
                          {r.profile?.last_event_at
                            ? new Date(r.profile.last_event_at).toLocaleDateString()
                            : "—"}
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">
                          {r.profile?.availability_status ?? "unknown"}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-1">
                            <IconButton
                              title="Request Availability"
                              onClick={() => {
                                setSelected(r.therapist);
                                setDialog("request");
                              }}
                            >
                              <Mail className="h-4 w-4" />
                            </IconButton>
                            <IconButton
                              title="Schedule Event"
                              onClick={() => {
                                setSelected(r.therapist);
                                setDialog("schedule");
                              }}
                            >
                              <CalendarPlus className="h-4 w-4" />
                            </IconButton>
                            <IconButton
                              title="View Responses"
                              onClick={() => {
                                setSelected(r.therapist);
                                setDialog("responses");
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </IconButton>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-10 text-center text-sm text-muted-foreground">
                        No therapists match those filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === "calendar" && <CalendarView rows={rows} />}

      {selected && dialog === "request" && (
        <RequestDialog therapist={selected} onClose={() => setDialog(null)} />
      )}
      {selected && dialog === "schedule" && (
        <ScheduleDialog therapist={selected} onClose={() => setDialog(null)} />
      )}
      {selected && dialog === "responses" && (
        <ResponsesDialog therapist={selected} onClose={() => setDialog(null)} />
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    none: "bg-muted text-muted-foreground",
    pending: "bg-amber-100 text-amber-800",
    responded: "bg-emerald-100 text-emerald-800",
    declined: "bg-rose-100 text-rose-800",
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs ${map[status] ?? map.none}`}>
      {status === "none" ? "Not contacted" : status}
    </span>
  );
}

function IconButton({
  children,
  title,
  onClick,
}: {
  children: React.ReactNode;
  title: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border text-foreground hover:bg-accent"
    >
      {children}
    </button>
  );
}

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/50 px-4 py-8">
      <div className="w-full max-w-lg rounded-2xl border border-border bg-card p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-serif text-lg text-foreground">{title}</h3>
          <button onClick={onClose} className="text-sm text-muted-foreground hover:text-foreground">
            Close
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function RequestDialog({ therapist, onClose }: { therapist: Therapist; onClose: () => void }) {
  const qc = useQueryClient();
  const reqFn = useServerFn(requestTherapistAvailability);
  const mutation = useMutation({
    mutationFn: () =>
      reqFn({
        data: {
          therapist_id: therapist.id,
          name: therapist.name,
          email: therapist.contactEmail,
          country: therapist.country,
          specialism: therapist.specialties.slice(0, 3).join(", "),
        },
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["therapist-event-profiles"] });
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const link = `${origin}/therapist-availability/${res.response_token}`;
      const subject = "Recovery Trauma™ Event Availability Request";
      const body = `Hello ${therapist.name},

I hope you're doing well.

As part of our upcoming Recovery Trauma™ events and workshops, I would love to learn more about your availability and the types of sessions you would be interested in offering.

Please share your availability using the secure form below:

${link}

We'd love to hear about:
• Workshop topic(s)
• Session title ideas
• Preferred format
• Approximate duration
• Time zone
• Days available
• Preferred times
• One-off or recurring
• Live or pre-recorded

We are currently planning future educational workshops, support sessions, and community events.

Thank you for being part of Recovery Trauma™.

With gratitude,

Jana Burkhardt
Founder, Recovery Trauma™`;
      const mailto = `mailto:${encodeURIComponent(therapist.contactEmail)}?subject=${encodeURIComponent(
        subject,
      )}&body=${encodeURIComponent(body)}`;
      window.open(mailto, "_blank");
      toast.success("Request recorded. Your email client should now open.");
      onClose();
    },
    onError: (e: unknown) => toast.error((e as Error).message),
  });

  return (
    <Modal title="Request Availability" onClose={onClose}>
      <p className="text-sm text-muted-foreground">
        This creates a tracked availability request for{" "}
        <span className="font-medium text-foreground">{therapist.name}</span> and
        opens your email client with the pre-written message and a secure
        response link.
      </p>
      <div className="mt-4 flex justify-end gap-2">
        <button
          onClick={onClose}
          className="rounded-md border border-border px-3 py-2 text-sm"
        >
          Cancel
        </button>
        <button
          disabled={mutation.isPending}
          onClick={() => mutation.mutate()}
          className="rounded-md bg-[var(--gold-deep)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
        >
          {mutation.isPending ? "Preparing…" : "Send Request"}
        </button>
      </div>
    </Modal>
  );
}

function ScheduleDialog({ therapist, onClose }: { therapist: Therapist; onClose: () => void }) {
  const qc = useQueryClient();
  const schedule = useServerFn(scheduleTherapistEvent);
  const [form, setForm] = useState({
    title: "",
    event_type: "Workshop",
    scheduled_at: "",
    duration_minutes: 60,
    allow_recording: false,
    notes: "",
  });

  const mutation = useMutation({
    mutationFn: () =>
      schedule({
        data: {
          therapist_id: therapist.id,
          ...form,
          scheduled_at: new Date(form.scheduled_at).toISOString(),
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["therapist-event-profiles"] });
      toast.success("Event scheduled");
      onClose();
    },
    onError: (e: unknown) => toast.error((e as Error).message),
  });

  return (
    <Modal title={`Schedule Event — ${therapist.name}`} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
        className="space-y-3"
      >
        <input
          required
          placeholder="Event title"
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
        />
        <div className="grid grid-cols-2 gap-3">
          <select
            className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            value={form.event_type}
            onChange={(e) => setForm({ ...form, event_type: e.target.value })}
          >
            {["Workshop", "Live Q&A", "Support Group", "Webinar", "Guest Speaker", "Meditation", "Yoga", "Other"].map(
              (t) => (
                <option key={t}>{t}</option>
              ),
            )}
          </select>
          <input
            type="number"
            min={15}
            max={600}
            className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            value={form.duration_minutes}
            onChange={(e) => setForm({ ...form, duration_minutes: Number(e.target.value) })}
          />
        </div>
        <input
          required
          type="datetime-local"
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          value={form.scheduled_at}
          onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
        />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.allow_recording}
            onChange={(e) => setForm({ ...form, allow_recording: e.target.checked })}
          />
          Allow replay recording
        </label>
        <textarea
          placeholder="Notes"
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border px-3 py-2 text-sm"
          >
            Cancel
          </button>
          <button
            disabled={mutation.isPending}
            className="rounded-md bg-[var(--gold-deep)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {mutation.isPending ? "Saving…" : "Save Event"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ResponsesDialog({ therapist, onClose }: { therapist: Therapist; onClose: () => void }) {
  const fn = useServerFn(listTherapistResponses);
  const { data, isLoading } = useQuery({
    queryKey: ["therapist-responses", therapist.id],
    queryFn: () => fn({ data: { therapist_id: therapist.id } }),
  });

  return (
    <Modal title={`Responses — ${therapist.name}`} onClose={onClose}>
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !data?.responses.length ? (
        <p className="text-sm text-muted-foreground">No responses yet.</p>
      ) : (
        <div className="max-h-[60vh] space-y-4 overflow-y-auto">
          {data.responses.map((r) => (
            <div key={r.id} className="rounded-lg border border-border p-3 text-sm">
              <p className="text-xs text-muted-foreground">
                {new Date(r.created_at).toLocaleString()}
              </p>
              {r.session_title && <p className="mt-1 font-medium">{r.session_title}</p>}
              {r.category && <p className="text-xs text-muted-foreground">{r.category}</p>}
              {r.workshop_description && <p className="mt-2">{r.workshop_description}</p>}
              <dl className="mt-2 grid grid-cols-2 gap-1 text-xs">
                <dt className="text-muted-foreground">Time zone</dt>
                <dd>{r.time_zone || "—"}</dd>
                <dt className="text-muted-foreground">Days</dt>
                <dd>{r.days_available?.join(", ") || "—"}</dd>
                <dt className="text-muted-foreground">Preferred time</dt>
                <dd>{r.preferred_time || "—"}</dd>
                <dt className="text-muted-foreground">Event types</dt>
                <dd>{r.event_types?.join(", ") || "—"}</dd>
                <dt className="text-muted-foreground">Recording</dt>
                <dd>{r.allow_recording ? "Allowed" : "Live only"}</dd>
              </dl>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

function CalendarView({ rows }: { rows: Row[] }) {
  const buckets = useMemo(() => {
    const out = { responded: [] as Row[], pending: [] as Row[], none: [] as Row[] };
    rows.forEach((r) => {
      const s = r.latestRequest?.status ?? "none";
      if (s === "responded") out.responded.push(r);
      else if (s === "pending") out.pending.push(r);
      else out.none.push(r);
    });
    return out;
  }, [rows]);

  return (
    <div className="grid gap-4 md:grid-cols-3">
      <Column title="Available (Responded)" color="bg-emerald-50 border-emerald-200" rows={buckets.responded} />
      <Column title="Pending (Awaiting Reply)" color="bg-amber-50 border-amber-200" rows={buckets.pending} />
      <Column title="Not Contacted" color="bg-rose-50 border-rose-200" rows={buckets.none} />
    </div>
  );
}

function Column({ title, color, rows }: { title: string; color: string; rows: Row[] }) {
  return (
    <div className={`rounded-2xl border p-4 ${color}`}>
      <h3 className="mb-3 text-sm font-semibold text-foreground">
        {title} <span className="text-xs text-muted-foreground">({rows.length})</span>
      </h3>
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r.therapist.id} className="rounded-md bg-background/70 p-2 text-sm">
            <p className="font-medium text-foreground">{r.therapist.name}</p>
            <p className="text-xs text-muted-foreground">{r.therapist.country}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}
